import {
  ActionBarPrimitive,
  AuiIf,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAuiState,
  useThreadViewport,
} from '@assistant-ui/react';
import { StreamdownTextPrimitive } from '@assistant-ui/react-streamdown';
import {
  faArrowDown,
  faArrowRotateRight,
  faArrowUp,
  faCopy,
  faStop,
  faThumbsDown,
} from '@fortawesome/pro-light-svg-icons';
import {
  Action,
  Badge,
  cn,
  Combobox,
  CorsoIcon,
  Icon,
  Modal,
  SimpleTooltip,
  TextAreaInput,
  TextInput,
} from 'corso-admin-ui';
import { useQuery } from '@tanstack/react-query';
import { isTruthy, PrimaryEntity, Sidekick } from 'corso-types';
import { startOfDay, subDays } from 'date-fns';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { match } from 'ts-pattern';
import { claimQueryOptions } from '~/features/claimReview/ClaimReviewProvider';
import { registrationQueryOptions } from '~/features/registrationReview/RegistrationReviewProvider';
import useIsAdmin from '~/hooks/useIsAdmin';
import { useStoreId } from '~/providers/StoreUserProvider';
import { useUser } from '~/providers/UserProvider';
import { ErrorResponse } from '~/utils/errorResponse';
import { groupBy } from '~/utils/map';
import { useCorsoAiChat } from './CorsoAiChat';
import {
  pageIcon,
  pageLabel,
  sidekickCopy,
  type StarterPrompt,
  starterPrompts,
} from './sidekickCopy';
import { sidekickToolUis } from './SidekickToolUis';

// Streamdown renders and styles the markdown; only links differ: a Corso app link (a setting, a record) opens in the app beside the chat
const markdownComponents = {
  a: ({ href = '', children }: React.ComponentProps<'a'>) => {
    const isExternal =
      new URL(href, window.location.href).origin !== window.location.origin;
    return (
      <Link
        to={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className="text-corso-blue-700 underline underline-offset-2 hover:text-corso-blue-500"
      >
        {children}
      </Link>
    );
  },
};

function MarkdownText() {
  return (
    <StreamdownTextPrimitive
      // text arrives in bursts; reveal it at an even pace instead of a paragraph landing at once
      smooth
      components={markdownComponents}
      containerClassName="text-sm leading-relaxed"
    />
  );
}

function Thinking() {
  return (
    <div className="flex items-center gap-1 py-0.5">
      <Icon icon="aiInline" className="size-3 animate-pulse text-neutral-500" />
      <p className="animate-pulse text-xs">
        {/* oxlint-disable-next-line typescript/no-misused-spread -- static set of known spread-allowed characters */}
        {[...sidekickCopy.thinking].map((character, i) => (
          <span
            // oxlint-disable-next-line react/no-array-index-key -- static set of characters, index is stable
            key={i}
            className="inline-block animate-in text-neutral-500 delay-(--delay) duration-200 fade-in"
            style={{
              '--delay': `${i * 0.1}s`,
            }}
            aria-hidden="true"
          >
            {character}
          </span>
        ))}
      </p>
    </div>
  );
}

function MessageFeedback() {
  const { conversationId, conversation, updateFeedback } = useCorsoAiChat();
  // the streamed message id is its stored id, so feedback works before the conversation refetches
  const messageId = Number(useAuiState(({ message }) => message.id));
  const stored = conversation?.messages.find((m) => m.id === messageId);
  const [comment, setComment] = useState(stored?.userComment ?? '');
  const [sentiment, setSentiment] = useState(
    stored?.userSentiment ?? 'Neutral',
  );

  const [showCommentModal, setShowCommentModal] = useState(false);

  if (!conversationId) return null;

  if (stored?.userSentiment && stored.userComment) {
    return (
      <Action disabled>
        <span className="text-xs">{sidekickCopy.feedback.submitted}</span>
      </Action>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <SimpleTooltip content={sidekickCopy.feedback.helpful} asChild>
        <Action
          icon="positiveFeedback"
          accessibilityLabel={sidekickCopy.feedback.toggleHelpful}
          variant="ghost"
          pressed={sentiment === 'Positive'}
          hidden={sentiment === 'Negative'}
          onClick={() => {
            const updatedSentiment =
              sentiment === 'Positive' ? 'Neutral' : 'Positive';
            setSentiment(updatedSentiment);
            updateFeedback({
              conversationId,
              messageId,
              userSentiment: updatedSentiment,
              userComment: null,
            });
          }}
        />
      </SimpleTooltip>
      <SimpleTooltip content={sidekickCopy.feedback.unhelpful} asChild>
        <Action
          icon={faThumbsDown}
          accessibilityLabel={sidekickCopy.feedback.toggleUnhelpful}
          variant="ghost"
          pressed={sentiment === 'Negative'}
          hidden={sentiment === 'Positive'}
          onClick={() => {
            const updatedSentiment =
              sentiment === 'Negative' ? 'Neutral' : 'Negative';
            setSentiment(updatedSentiment);
            updateFeedback({
              conversationId,
              messageId,
              userSentiment: updatedSentiment,
              userComment: null,
            });
          }}
        />
      </SimpleTooltip>
      <Action
        hidden={sentiment === 'Neutral'}
        onClick={() => setShowCommentModal(true)}
      >
        <span className="text-xs">{sidekickCopy.feedback.addComment}</span>
      </Action>
      <Modal
        title={sidekickCopy.feedback.title}
        description={sidekickCopy.feedback.description(sentiment)}
        show={showCommentModal}
        onClose={() => setShowCommentModal(false)}
        actions={
          <Action
            type="submit"
            variant="primary"
            onClick={() => {
              setShowCommentModal(false);
              const trimmed = comment.trim();
              if (!trimmed) return;
              updateFeedback({
                conversationId,
                messageId,
                userComment: trimmed,
                userSentiment: sentiment,
              });
            }}
          >
            {sidekickCopy.feedback.submit}
          </Action>
        }
      >
        <TextAreaInput
          id={`feedback-${messageId}`}
          label={sidekickCopy.feedback.label}
          labelVisuallyHidden
          required
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Modal>
    </div>
  );
}

function AssistantActions() {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      className="flex items-center gap-1"
    >
      <SimpleTooltip content={sidekickCopy.message.copy} asChild>
        <ActionBarPrimitive.Copy asChild>
          <Action
            variant="ghost"
            icon={faCopy}
            accessibilityLabel={sidekickCopy.message.copyLabel}
          />
        </ActionBarPrimitive.Copy>
      </SimpleTooltip>
      {/* the server can only replace the latest answer */}
      <AuiIf condition={({ message }) => message.isLast}>
        <SimpleTooltip content={sidekickCopy.message.regenerate} asChild>
          <ActionBarPrimitive.Reload asChild>
            <Action
              variant="ghost"
              icon={faArrowRotateRight}
              accessibilityLabel={sidekickCopy.message.regenerateLabel}
            />
          </ActionBarPrimitive.Reload>
        </SimpleTooltip>
      </AuiIf>
      <MessageFeedback />
    </ActionBarPrimitive.Root>
  );
}

function UserMessage() {
  return (
    <MessagePrimitive.Root className="flex flex-col items-end">
      <div className="max-w-[85%] rounded-xl bg-corso-blue-600 px-3.5 py-3 text-sm leading-relaxed whitespace-pre-wrap text-white">
        <MessagePrimitive.Parts />
      </div>
    </MessagePrimitive.Root>
  );
}

function AssistantMessage() {
  return (
    // capped at the chat's width so a wide table scrolls within itself instead of the whole chat
    <MessagePrimitive.Root className="flex flex-col items-start gap-2 *:max-w-full">
      <MessagePrimitive.Parts
        components={{ Text: MarkdownText, tools: sidekickToolUis }}
      />
      <MessagePrimitive.Error>
        <p className="rounded-xl border border-red-600 bg-red-50 px-3.5 py-3 text-sm text-red-700">
          {sidekickCopy.message.failed}
        </p>
      </MessagePrimitive.Error>
      <AssistantActions />
    </MessagePrimitive.Root>
  );
}

// a request that fails before its answer starts has no message to carry the error
function RequestError() {
  const { error, retry, isBusy } = useCorsoAiChat();
  if (!error || isBusy) return null;
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-red-600 bg-red-50 px-3.5 py-2 text-sm text-red-700">
      <span>
        {error instanceof ErrorResponse && error.response.status === 429
          ? sidekickCopy.requestError.rateLimited
          : sidekickCopy.requestError.failed}
      </span>
      <Action variant="ghost" onClick={retry}>
        {sidekickCopy.requestError.retry}
      </Action>
    </div>
  );
}

const STARTER_PROMPTS_SHOWN = 3;

function Suggestions() {
  const { sharedPage } = useCorsoAiChat();
  const isAdmin = useIsAdmin();
  const kind = sharedPage?.kind ?? 'Anywhere';
  // a run of the page's prompts from a random start, picked again for each new chat or page
  const prompts = useMemo(() => {
    const forPage: StarterPrompt[] = starterPrompts[kind];
    const pool = forPage.filter((starter) => isAdmin || !starter.adminOnly);
    const start = Math.floor(Math.random() * pool.length);
    return Array.from(
      { length: Math.min(STARTER_PROMPTS_SHOWN, pool.length) },
      (_, index) => pool[(start + index) % pool.length]?.prompt,
    ).filter(isTruthy);
  }, [kind, isAdmin]);

  return (
    <div className="flex flex-wrap justify-center gap-2">
      {prompts.map((prompt) => (
        <ThreadPrimitive.Suggestion key={prompt} prompt={prompt} send asChild>
          <Action>
            <span className="text-xs">{prompt}</span>
          </Action>
        </ThreadPrimitive.Suggestion>
      ))}
    </div>
  );
}

function PageChip() {
  const { sharedPage, dismissPage } = useCorsoAiChat();
  const storeId = useStoreId();
  // the record page's own queries, so the chip reads the number its header shows without another request
  const isClaim =
    sharedPage?.kind === PrimaryEntity.Return ||
    sharedPage?.kind === PrimaryEntity.Warranty;
  const claim = useQuery({
    ...claimQueryOptions(storeId, isClaim ? sharedPage.id : undefined),
    enabled: isClaim,
  });
  const isRegistration = sharedPage?.kind === PrimaryEntity.Registration;
  const registration = useQuery({
    ...registrationQueryOptions(
      storeId,
      isRegistration ? sharedPage.id : undefined,
    ),
    enabled: isRegistration,
  });
  const label =
    sharedPage &&
    pageLabel(
      sharedPage,
      claim.data?.externalId ?? registration.data?.externalId,
    );
  if (!label) return null;
  return (
    <div className="flex items-center gap-1">
      <Badge variant="info">
        <Icon icon={pageIcon(sharedPage)} className="size-3" />
        <span className="first-letter:uppercase">{label}</span>
      </Badge>
      <SimpleTooltip content={sidekickCopy.composer.dismissPage} asChild>
        <Action
          variant="ghost"
          icon="close"
          accessibilityLabel={sidekickCopy.composer.dismissPage}
          onClick={dismissPage}
        />
      </SimpleTooltip>
    </div>
  );
}

const COMPOSER_MAX_ROWS = 5;

function ChatInput() {
  const { setOpen } = useCorsoAiChat();
  return (
    <ComposerPrimitive.Root className="flex flex-col gap-1">
      <PageChip />
      <div className="flex items-end gap-2">
        <div className="group grid flex-1 grid-cols-1 grid-rows-1 *:col-span-full *:row-span-full">
          <div className="z-0 scale-x-105 scale-y-130 ai-shimmer rounded-md mask-y-from-80% mask-x-from-80% mask-radial-at-center transition-transform duration-150 group-focus-within:scale-x-110 group-focus-within:scale-y-150" />
          <ComposerPrimitive.Input
            aria-label={sidekickCopy.composer.placeholder}
            placeholder={sidekickCopy.composer.placeholder}
            autoFocus
            maxRows={COMPOSER_MAX_ROWS}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
            className="z-10 flex w-full resize-none gap-1 rounded-md border-none px-3 py-1.5 text-neutral-800 ring-1 ring-neutral-300 sm:text-sm sm:leading-6"
          />
        </div>
        <AuiIf condition={({ thread }) => !thread.isRunning}>
          <ComposerPrimitive.Send asChild>
            <Action
              variant="primary"
              icon={faArrowUp}
              accessibilityLabel={sidekickCopy.composer.send}
            />
          </ComposerPrimitive.Send>
        </AuiIf>
        <AuiIf condition={({ thread }) => thread.isRunning}>
          <ComposerPrimitive.Cancel asChild>
            <Action
              icon={faStop}
              accessibilityLabel={sidekickCopy.composer.stop}
            />
          </ComposerPrimitive.Cancel>
        </AuiIf>
      </div>
    </ComposerPrimitive.Root>
  );
}

function ScrollToBottom() {
  const isAtBottom = useThreadViewport((viewport) => viewport.isAtBottom);
  if (isAtBottom) return null;
  return (
    <div className="absolute bottom-16 left-1/2 -translate-x-1/2">
      <ThreadPrimitive.ScrollToBottom asChild>
        <Action
          icon={faArrowDown}
          accessibilityLabel={sidekickCopy.message.scrollToBottom}
        />
      </ThreadPrimitive.ScrollToBottom>
    </div>
  );
}

const AFTERNOON_HOUR = 12;
const EVENING_HOUR = 17;

function getTimeOfDayGreeting() {
  const hour = new Date().getHours();
  if (hour < AFTERNOON_HOUR) return sidekickCopy.greeting.morning;
  if (hour < EVENING_HOUR) return sidekickCopy.greeting.afternoon;
  return sidekickCopy.greeting.evening;
}

function ConversationSwitcher() {
  const {
    conversation: current,
    conversations,
    changeConversation,
    isBusy,
  } = useCorsoAiChat();

  const conversationGroups = useMemo(() => {
    const today = startOfDay(new Date());
    const yesterday = subDays(today, 1);

    const sorted = [...conversations]
      .map((convo) => ({
        id: convo.id,
        name: convo.id === current?.id ? current.name : convo.name, // ensure current conversation is always up-to-date with its name, as the list only invalidates when changing
        compareDate:
          'createdOn' in convo
            ? new Date(convo.updatedOn ?? convo.createdOn) // operate on last update time, falling back to created, for sorting and grouping // ! assumed that updatedOn will be set when a conversation has new messages
            : new Date(), // for newly-created just consider them "now"
      }))
      .sort((a, b) => b.compareDate.getTime() - a.compareDate.getTime());
    // TODO need additional sorting based on latest message time to elevate recently interacted conversations

    // believed to already be sorted descending
    return groupBy(sorted, (convo) =>
      match(convo.compareDate)
        .when(
          (d) => d >= today,
          () => sidekickCopy.conversationGroup.today,
        )
        .when(
          (d) => d >= yesterday,
          () => sidekickCopy.conversationGroup.yesterday,
        )
        .otherwise(() => sidekickCopy.conversationGroup.earlier),
    );
  }, [conversations, current]);

  return (
    <Combobox
      label={current?.name || Sidekick.defaultConversationName}
      disabled={isBusy}
      emptyLabel={sidekickCopy.header.noConversations}
      value={current?.id ? `${current.id}` : undefined}
      options={[...conversationGroups.entries()].map(([group, convos]) => ({
        label: group,
        options: convos.map((convo) => ({
          value: `${convo.id}`,
          label: convo.name,
        })),
      }))}
      onChange={(value) => {
        const convo = conversations.find((c) => `${c.id}` === value);
        if (convo?.id) changeConversation(convo.id).catch(() => {});
      }}
    />
  );
}

function RenameConversation() {
  const { conversation, renameConversation, isBusy } = useCorsoAiChat();
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');

  if (!conversation) return null;

  const save = () => {
    const trimmed = name.trim();
    setShow(false);
    if (!trimmed || trimmed === conversation.name) return;
    renameConversation({ id: conversation.id, name: trimmed });
  };

  return (
    <>
      <SimpleTooltip content={sidekickCopy.header.rename} asChild>
        <Action
          accessibilityLabel={sidekickCopy.header.rename}
          onClick={() => {
            setName(conversation.name);
            setShow(true);
          }}
          variant="ghost"
          icon="edit"
          disabled={isBusy}
        />
      </SimpleTooltip>
      <Modal
        title={sidekickCopy.rename.title}
        show={show}
        onClose={() => setShow(false)}
        actions={
          <Action type="submit" variant="primary" onClick={save}>
            {sidekickCopy.rename.save}
          </Action>
        }
      >
        <TextInput
          id="sidekick-conversation-name"
          label={sidekickCopy.rename.name}
          labelVisuallyHidden
          required
          maxLength={Sidekick.CONVERSATION_NAME_MAX_LENGTH}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
          }}
        />
      </Modal>
    </>
  );
}

export default function CorsoAiSidekickChat() {
  const user = useUser();
  const { open, setOpen, changeConversation, isBusy, isLoadingConversation } =
    useCorsoAiChat();

  return (
    <aside
      className={cn(
        'hidden h-full flex-col gap-2 overflow-hidden rounded-t-lg bg-white lg:ml-1 lg:flex',
        open && 'flex',
      )}
    >
      {/* slightly smaller horizontal space for optical alignment */}
      {/* done as a grid to ensure sizing, but wrapping the elements with a span on the switcher overflows */}
      <header className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 px-3 pt-2">
        <ConversationSwitcher />
        <div className="flex items-center gap-1 justify-self-end">
          <RenameConversation />
          <SimpleTooltip content={sidekickCopy.header.newChat} asChild>
            <Action
              accessibilityLabel={sidekickCopy.header.newChat}
              onClick={() => {
                changeConversation(null).catch(() => {});
              }}
              variant="ghost"
              icon="add"
              disabled={isBusy}
            />
          </SimpleTooltip>
          <Action
            accessibilityLabel={sidekickCopy.header.close}
            onClick={() => setOpen(false)}
            variant="ghost"
            icon="close"
          />
        </div>
      </header>
      <ThreadPrimitive.Root className="relative flex flex-1 flex-col overflow-hidden">
        <ThreadPrimitive.Viewport className="flex flex-1 flex-col gap-3 overflow-y-auto px-4">
          {isLoadingConversation ? (
            <div className="flex flex-col gap-3" aria-busy="true">
              <div className="h-10 w-2/3 animate-pulse self-end rounded-xl bg-neutral-100" />
              <div className="h-24 w-full animate-pulse rounded-xl bg-neutral-100" />
              <div className="h-10 w-1/2 animate-pulse self-end rounded-xl bg-neutral-100" />
            </div>
          ) : (
            <>
              <ThreadPrimitive.Empty>
                <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
                  <CorsoIcon />
                  <div>
                    <p className="text-base text-neutral-500">
                      {getTimeOfDayGreeting()}
                      {user.firstName ? `, ${user.firstName}` : ''}
                    </p>
                    <p className="text-lg font-semibold text-neutral-800">
                      {sidekickCopy.greeting.question}
                    </p>
                  </div>
                  <Suggestions />
                </div>
              </ThreadPrimitive.Empty>
              <ThreadPrimitive.Messages
                components={{ UserMessage, AssistantMessage }}
              />
              {/* only until the answer starts: after that the streaming text and tool steps show progress */}
              <AuiIf
                condition={({ thread }) => {
                  const last = thread.messages.at(-1);
                  return (
                    thread.isRunning &&
                    (last?.role !== 'assistant' || last.content.length === 0)
                  );
                }}
              >
                <Thinking />
              </AuiIf>
              <RequestError />
            </>
          )}
        </ThreadPrimitive.Viewport>
        <ScrollToBottom />
        <footer className="px-4 pb-2">
          <ChatInput />
        </footer>
      </ThreadPrimitive.Root>
    </aside>
  );
}
